import { fromUnixTime } from 'date-fns';
import { format } from 'date-fns-tz';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useMemo } from 'react';
import styled from 'styled-components';

import { GlobalModalContext } from '@context/globalModalContext';
import colors from '@general/colors';
import { getTimeRangeText } from '@general/dates';
import fonts from '@general/fonts';
import { formatMoney } from '@general/helpers/currency.helpers';
import { BodySmall, ButtonRedesign, H3, H4 } from '@styled';

export const useTrusteeEventSessionModal = ({
    fees,
    id,
    isRegistered,
    registrantClass,
    title,
    sessionStatus,
    sessionStartUnix,
    sessionEndUnix,
    sessionRoom,
    description,
    eventCode,
}) => {
    const [, setIsModalOpen, setModalChildren, setModalOuterClass] = useContext(GlobalModalContext);

    useEffect(() => {
        setModalOuterClass('trustee-session-detail-modal');
    }, [setModalOuterClass]);

    const openModal = () => {
        setModalChildren(() => (
            <TrusteeEventSessionModal
                fees={fees}
                id={id}
                isRegistered={isRegistered}
                registrantClass={registrantClass}
                title={title}
                sessionStatus={sessionStatus}
                sessionStartUnix={sessionStartUnix}
                sessionEndUnix={sessionEndUnix}
                sessionRoom={sessionRoom}
                description={description}
                eventCode={eventCode}
                closeModal={() => setIsModalOpen(false)}
            />
        ));

        setIsModalOpen(true);
    };

    return { openModal };
};

const DetailsTextStyled = styled(BodySmall)`
    color: ${colors.darkGray};
`;

const Description = styled.p`
    p {
        color: ${colors.secondary};
        font-family: ${fonts.sans};
        font-size: 21px;
        font-style: normal;
        font-weight: 400;
        letter-spacing: 0.05px;
        line-height: 28px;
        margin: 0;
    }
`;

const TrusteeEventSessionModal = ({
    fees,
    id,
    isRegistered,
    registrantClass,
    title,
    sessionStartUnix,
    sessionEndUnix,
    sessionRoom,
    description,
    eventCode,
}) => {
    const feeForRegistrantClass = useMemo(() => {
        if (!fees || !registrantClass || registrantClass.length === 0) {
            return null;
        }

        let fee = fees.find(f => registrantClass.includes(f.registrant_class));
        if (!fee) {
            fee = fees.find(f => f.registrant_class === 'NM');
        }

        return fee;
    }, [fees, registrantClass]);

    return (
        <div>
            <div className="mb-2rem">
                <H3>{title}</H3>
                {feeForRegistrantClass && <H4>{formatMoney(feeForRegistrantClass.price, 'US')}</H4>}
            </div>

            <div className="mb-2rem">
                <DetailsTextStyled>{format(fromUnixTime(sessionStartUnix), 'EEEE, MMMM d')}</DetailsTextStyled>
                <DetailsTextStyled>
                    {getTimeRangeText(fromUnixTime(sessionStartUnix), fromUnixTime(sessionEndUnix))}
                </DetailsTextStyled>

                {sessionRoom && <DetailsTextStyled>{sessionRoom}</DetailsTextStyled>}
            </div>

            {description && <Description className="mb-2rem" dangerouslySetInnerHTML={{ __html: description }} />}

            {fees && !isRegistered && (
                <ButtonRedesign
                    as="a"
                    href={`/cart/view?event=${eventCode}&session=${id}`}
                    style={{ paddingTop: '14px' }}
                >
                    Register Now
                </ButtonRedesign>
            )}
        </div>
    );
};

TrusteeEventSessionModal.propTypes = {
    fees: PropTypes.arrayOf(PropTypes.object),
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    isRegistered: PropTypes.bool.isRequired,
    registrantClass: PropTypes.arrayOf(PropTypes.string).isRequired,
    title: PropTypes.string.isRequired,
    sessionStartUnix: PropTypes.number,
    sessionEndUnix: PropTypes.number,
    sessionRoom: PropTypes.string,
    description: PropTypes.string,
    eventCode: PropTypes.string.isRequired,
};

export default TrusteeEventSessionModal;
